import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { CollapsibleDescriptionCard } from "@features/investments/components/collapsible-description-card";
import { InvestmentsAccountMenu } from "@features/investments/components/account-menu";
import { InvestmentsPageState } from "@features/investments/components/page-state";
import { QuoteSummaryStrip } from "@features/investments/components/quote-summary-strip";
import {
  useETFDetailQuery,
  useInstrumentCatalogQuery,
  useInvestmentsAccessQuery,
  useInvestmentQuoteQuery,
  useRefreshInvestmentMutation,
} from "@features/investments/queries";
import { GlossaryTerm } from "@features/investments/glossary/term";
import { getDetailDateMeta } from "@features/investments/screens/workspace-shared";
import { createInitialETFDetailState, etfDetailReducer } from "@features/investments/state";
import { buildETFPriceSeries, buildQueryErrorMessage, ETFPricePoint } from "@features/investments/transforms";
import "@views/investments/index.scss";
import { equityRoute } from "@features/investments/routes";

const InvestmentETFTickerView = (): JSX.Element => {
  const params = useParams();
  const navigate = useNavigate();
  const ticker = (params.ticker || "").toUpperCase();

  const [state, dispatch] = React.useReducer(etfDetailReducer, undefined, createInitialETFDetailState);
  const { etfTab, refreshMessage, refreshError } = state;
  const catalogQuery = useInstrumentCatalogQuery(ticker);
  const detailQuery = useETFDetailQuery(ticker, catalogQuery.data?.instrumentType !== "EQUITY");
  const accessQuery = useInvestmentsAccessQuery();
  const canUseFmp = accessQuery.data?.canUseFmp === true;
  const quoteRestricted = accessQuery.data?.authenticated === true && accessQuery.data?.authorized === true && !canUseFmp;
  const quoteQuery = useInvestmentQuoteQuery(ticker, canUseFmp);
  const refreshMutation = useRefreshInvestmentMutation();
  const data = detailQuery.data ?? null;
  const detailDateMeta = data ? getDetailDateMeta(data.refreshedAt, data.asOfDate) : null;
  const quote = quoteQuery.data ?? null;
  const loading = ticker === "" ? false : (catalogQuery.isLoading || detailQuery.isLoading);
  const error = ticker === ""
    ? "Ticker is missing"
    : catalogQuery.error
      ? buildQueryErrorMessage(catalogQuery.error, "Failed to resolve instrument")
      : detailQuery.error
      ? buildQueryErrorMessage(detailQuery.error, "Failed to load ETF details")
      : "";
  const quoteLoading = ticker === "" ? false : quoteQuery.isLoading;
  const quoteError = ticker === ""
    ? "Ticker is missing"
    : quoteRestricted
      ? ""
    : quoteQuery.error
      ? buildQueryErrorMessage(quoteQuery.error, "Quote data unavailable")
      : "";
  const quoteLastSuccessAt = quoteQuery.data ? new Date(quoteQuery.dataUpdatedAt).toISOString() : null;
  const refreshing = refreshMutation.isPending;
  const backHref = accessQuery.data?.isGod ? "/investments" : "/investments/coverage";
  const backLabel = accessQuery.data?.isGod ? "Back to Command" : "Back to Coverage";

  React.useEffect(() => {
    if (catalogQuery.data?.instrumentType === "EQUITY") {
      navigate(equityRoute(ticker), { replace: true });
    }
  }, [catalogQuery.data, navigate, ticker]);

  React.useEffect(() => {
    dispatch({ type: "tickerChanged" });
  }, [ticker]);

  const etfData = data?.etf;
  const companyDescription = (data?.companyDescription || "").trim();

  const etfPriceSeries = React.useMemo<ETFPricePoint[]>(() => {
    return buildETFPriceSeries(data);
  }, [data]);

  const etfReturns = React.useMemo(() => {
    return {
      oneMonth: computeTrailingReturn(etfPriceSeries, 21),
      threeMonth: computeTrailingReturn(etfPriceSeries, 63),
      sixMonth: computeTrailingReturn(etfPriceSeries, 126),
      oneYear: computeTrailingReturn(etfPriceSeries, 252),
    };
  }, [etfPriceSeries]);

  async function handleRefreshTicker(): Promise<void> {
    if (!ticker || refreshing || !canUseFmp) {
      return;
    }
    dispatch({ type: "clearRefreshFeedback" });
    try {
      const result = await refreshMutation.mutateAsync(ticker);
      dispatch({
        type: "setRefreshMessage",
        value: `Refresh complete (${result.target}): synced=${result.sync.updated ? "updated" : "already-fresh"}, uploaded=${result.bucketSync.uploaded} file(s).`,
      });
      await Promise.all([detailQuery.refetch(), quoteQuery.refetch()]);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Ticker refresh failed";
      dispatch({ type: "setRefreshError", value: message });
    }
  }

  return (
    <div className="investments-page">
      <div className="investments-shell">
        <header className="investments-header">
          <div>
            <h1>{`${ticker || "Ticker"} ETF`}</h1>
            <p>{data?.companyName || "ETF detail view"} {detailDateMeta?.value && detailDateMeta.value !== "N/A" ? `• ${detailDateMeta.inlineLabel} ${detailDateMeta.value}` : ""}</p>
          </div>
          <div className="investments-header-right">
            <div className="investments-header-toolbar">
              <div className="investments-header-actions">
                {canUseFmp && (
                  <button
                    className="investments-link investments-link-button"
                    type="button"
                    disabled={refreshing || loading || !!error}
                    onClick={() => void handleRefreshTicker()}
                  >
                    {refreshing ? "Refreshing..." : "Refresh Ticker"}
                  </button>
                )}
                <Link className="investments-link" to={backHref}>{backLabel}</Link>
              </div>
              <InvestmentsAccountMenu />
            </div>
            <div className="investments-header-note">
              {quoteRestricted ? "Snapshot Research uses cached data only. " : ""}
              <Link className="investments-link" to="/investments/reference/method">Method</Link>
              {" · "}
              <Link className="investments-link" to="/investments/reference/glossary">Glossary</Link>
            </div>
            {canUseFmp && !loading && !error && data && (
              <QuoteSummaryStrip
                ticker={ticker}
                quote={quote}
                loading={quoteLoading}
                error={quoteError}
                lastSuccessAt={quoteLastSuccessAt}
                restricted={quoteRestricted}
                onRefresh={() => void quoteQuery.refetch()}
                compact
              />
            )}
          </div>
        </header>

        {refreshMessage && <div className="ticker-refresh-status" role="status" aria-live="polite">{refreshMessage}</div>}
        {refreshError && <div className="ticker-refresh-status error" role="alert">{refreshError}</div>}

        {loading && <InvestmentsPageState message="Loading ETF data..." />}
        {!loading && error && <InvestmentsPageState message={`Failed to load: ${error}`} />}

        {!loading && !error && data && (
          <>
            <div className="investments-tab-toggle">
              <button className={etfTab === "overview" ? "active" : ""} onClick={() => dispatch({ type: "setETFTab", value: "overview" })}>
                Overview
              </button>
              <button className={etfTab === "composition" ? "active" : ""} onClick={() => dispatch({ type: "setETFTab", value: "composition" })}>
                Composition
              </button>
              <button className={etfTab === "performance" ? "active" : ""} onClick={() => dispatch({ type: "setETFTab", value: "performance" })}>
                Performance
              </button>
            </div>

            <CollapsibleDescriptionCard title="What This ETF Does" description={companyDescription} />

            <section className="kpi-rail-grid">
              <KPI label="Asset Class" value={etfData?.profile?.assetClass || "N/A"} />
              <KPI label="ETF Company" value={etfData?.profile?.etfCompany || "N/A"} />
              <KPI label="Expense Ratio" value={formatETFPercentValue(etfData?.profile?.expenseRatio ?? null)} />
              <KPI label="AUM" value={formatCurrencyNullable(etfData?.profile?.assetsUnderManagement ?? null)} />
              <KPI label="NAV" value={formatCurrencyNullable(etfData?.profile?.nav ?? null, etfData?.profile?.navCurrency || "USD")} />
              <KPI label="Avg Volume" value={formatNumberCompact(etfData?.profile?.avgVolume ?? null)} />
              <KPI label="Holdings" value={formatNumberCompact(etfData?.profile?.holdingsCount ?? null)} />
              <KPI label="Domicile" value={etfData?.profile?.domicile || "N/A"} />
              <KPI label="ISIN" value={etfData?.profile?.isin || "N/A"} />
              <KPI label="Inception Date" value={etfData?.profile?.inceptionDate || "N/A"} />
              <KPI label="Live Price" value={formatCurrencyNullable(quote?.price ?? null)} />
              <KPI label="Daily Move" value={`${formatSignedCurrencyNullable(quote?.change ?? null)} (${formatSignedPercentNullable(quote?.changePercent ?? null)})`} />
            </section>

            {etfData?.warnings && etfData.warnings.length > 0 && (
              <details className="warning-banner">
                <summary>
                  <span>ETF Data Warnings</span>
                  <span className="warning-count">{etfData.warnings.length}</span>
                </summary>
                <div className="warning-groups">
                  <div className="warning-group">
                    <h4>Provider Coverage</h4>
                    <ul>
                      {etfData.warnings.map((warning: string) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </details>
            )}

            {etfTab === "overview" && (
              <section className="report-card">
                <h3>ETF Profile</h3>
                <div className="report-grid two-up report-grid-dense">
                  <KeyValue label="Name" value={data.companyName || "N/A"} />
                  <KeyValue label="Symbol" value={etfData?.profile?.symbol || data.ticker} />
                  <KeyValue label="Asset Class" value={etfData?.profile?.assetClass || "N/A"} />
                  <KeyValue label="ETF Company" value={etfData?.profile?.etfCompany || "N/A"} />
                  <KeyValue label="Expense Ratio" value={formatETFPercentValue(etfData?.profile?.expenseRatio ?? null)} />
                  <KeyValue label="Assets Under Management" value={formatCurrencyNullable(etfData?.profile?.assetsUnderManagement ?? null)} />
                  <KeyValue label="NAV" value={formatCurrencyNullable(etfData?.profile?.nav ?? null, etfData?.profile?.navCurrency || "USD")} />
                  <KeyValue label="NAV Currency" value={etfData?.profile?.navCurrency || "N/A"} />
                  <KeyValue label="Average Volume" value={formatNumberCompact(etfData?.profile?.avgVolume ?? null)} />
                  <KeyValue label="Holdings Count" value={formatNumberCompact(etfData?.profile?.holdingsCount ?? null)} />
                  <KeyValue label="ISIN" value={etfData?.profile?.isin || "N/A"} />
                  <KeyValue label="CUSIP" value={etfData?.profile?.securityCusip || "N/A"} />
                  <KeyValue label="Domicile" value={etfData?.profile?.domicile || "N/A"} />
                  <KeyValue label="Inception Date" value={etfData?.profile?.inceptionDate || "N/A"} />
                  <KeyValue
                    label="Actively Trading"
                    value={etfData?.profile?.isActivelyTrading === null || etfData?.profile?.isActivelyTrading === undefined
                      ? "N/A"
                      : boolLabel(Boolean(etfData.profile.isActivelyTrading))}
                  />
                  <KeyValue label={detailDateMeta?.fieldLabel ?? "Refreshed Date"} value={detailDateMeta?.value ?? "N/A"} />
                </div>
                {etfData?.profile?.website && (
                  <p className="buy-levels-note">
                    Source: <a href={etfData.profile.website} target="_blank" rel="noreferrer">{etfData.profile.website}</a>
                  </p>
                )}
              </section>
            )}

            {etfTab === "composition" && (
              <section className="report-card buy-levels-panel">
                <div className="buy-levels-panel-section">
                  <h3>Sector Weights</h3>
                  {(etfData?.sectorWeights || []).length > 0 ? (
                    <div className="buy-levels-table-wrap">
                      <table className="buy-levels-table">
                        <thead>
                          <tr>
                            <th>Sector</th>
                            <th className="numeric-col">Weight</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(etfData?.sectorWeights || []).map((weight: any, index: number) => (
                            <tr key={`sector-${weight.name}-${index}`}>
                              <td>{weight.name || "N/A"}</td>
                              <td className="numeric-col">{formatETFPercentValue(weight.weightPercentage ?? null)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="chart-empty">No sector weighting data available.</div>
                  )}
                </div>

                <div className="buy-levels-panel-section">
                  <h3>Country Weights</h3>
                  {(etfData?.countryWeights || []).length > 0 ? (
                    <div className="buy-levels-table-wrap">
                      <table className="buy-levels-table">
                        <thead>
                          <tr>
                            <th>Country</th>
                            <th className="numeric-col">Weight</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(etfData?.countryWeights || []).map((weight: any, index: number) => (
                            <tr key={`country-${weight.name}-${index}`}>
                              <td>{weight.name || "N/A"}</td>
                              <td className="numeric-col">{formatETFPercentValue(weight.weightPercentage ?? null)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="chart-empty">No country weighting data available.</div>
                  )}
                </div>

              </section>
            )}

            {etfTab === "performance" && (
              <>
                <section className="kpi-rail-grid">
                  <KPI label="1M Return" value={formatSignedPercentNullable(etfReturns.oneMonth)} />
                  <KPI label="3M Return" value={formatSignedPercentNullable(etfReturns.threeMonth)} />
                  <KPI label="6M Return" value={formatSignedPercentNullable(etfReturns.sixMonth)} />
                  <KPI label="1Y Return" value={formatSignedPercentNullable(etfReturns.oneYear)} />
                </section>
                <section className="report-card">
                  <h3>Price History (1Y)</h3>
                  {etfPriceSeries.length > 0 ? (
                    <div className="investments-chart-scroll">
                      <div className="investments-chart-min">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={etfPriceSeries}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2a3552" />
                            <XAxis dataKey="date" tick={{ fill: "#c4d3f2", fontSize: 11 }} />
                            <YAxis
                              tick={{ fill: "#c4d3f2", fontSize: 11 }}
                              tickFormatter={(value) => formatAxisPrice(Number(value))}
                            />
                            <Tooltip formatter={(value: unknown) => (typeof value === "number" ? formatCurrency(value) : "N/A")} />
                            <Line
                              type="monotone"
                              dataKey="close"
                              stroke="#60a5fa"
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <div className="chart-empty">No ETF price history available.</div>
                  )}
                </section>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function KPI({ label, value }: { label: string; value: string; }): JSX.Element {
  return (
    <div className="investments-kpi">
      <div className="label">
        <GlossaryTerm label={label} variant="kpi" className="metric-label-text" />
      </div>
      <div className="value">{value}</div>
    </div>
  );
}

function KeyValue({ label, value }: { label: string; value: string; }): JSX.Element {
  return (
    <div className="kv-item">
      <span className="k"><GlossaryTerm label={label} variant="kv" className="metric-label-text" /></span>
      <span className="v">{value}</span>
    </div>
  );
}

function boolLabel(value: boolean): string {
  return value ? "YES" : "NO";
}

function formatCurrency(value: number, currency = "USD"): string {
  if (!Number.isFinite(value)) {
    return "-";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatCurrencyNullable(value: number | null, currency = "USD"): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  return formatCurrency(value, currency);
}

function formatSignedCurrencyNullable(value: number | null, currency = "USD"): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  const abs = formatCurrency(Math.abs(value), currency);
  if (value > 0) {
    return `+${abs}`;
  }
  if (value < 0) {
    return `-${abs}`;
  }
  return abs;
}

function formatSignedPercentNullable(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  if (value > 0) {
    return `+${value.toFixed(2)}%`;
  }
  if (value < 0) {
    return `${value.toFixed(2)}%`;
  }
  return `${value.toFixed(2)}%`;
}

function formatETFPercentValue(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "N/A";
  }
  return `${value.toFixed(2)}%`;
}

function formatNumberCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "N/A";
  }
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value);
}

function formatAxisPrice(value: number): string {
  if (!Number.isFinite(value)) {
    return "0";
  }
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toFixed(2);
}

function computeTrailingReturn(points: ETFPricePoint[], lookbackDays: number): number | null {
  if (lookbackDays <= 0 || points.length <= lookbackDays) {
    return null;
  }
  const endPoint = points[points.length - 1];
  const startPoint = points[points.length - 1 - lookbackDays];
  if (!endPoint || !startPoint || !Number.isFinite(endPoint.close) || !Number.isFinite(startPoint.close) || startPoint.close <= 0) {
    return null;
  }
  return ((endPoint.close / startPoint.close) - 1) * 100;
}

export default InvestmentETFTickerView;
